import _ from 'lodash'
import { i18n } from '@lingui/core'
import 'react-quill/dist/quill.snow.css'
import ReactMarkdown from 'react-markdown'

import { Box, Paper, Typography } from '@mui/material'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'

import { ChatMessage, ChatRoom } from '@/core/models/chat-room.model'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { MessageStatusIcon } from './MessageStatusIcon'
import MessageReaders from './MessageReaders'
import { Staff } from '@/core/models/staff.model'

export function getUserName(userId: string, chatRoomUsers?: ChatRoom['users'], isCompleted?: boolean): string {
  const user = chatRoomUsers?.find((user) => user._id === userId)

  if (!user) return 'Utilisateur inconnu'

  return isCompleted
    ? `${user.userInfo.name} ${user.userInfo.surname}`.trim()
    : `${user.userInfo.name.charAt(0).toUpperCase()}.${user.userInfo.surname.charAt(0).toUpperCase()}`.trim()
}

type MessageProps = {
  chatRoomUsers?: ChatRoom['users']
  isLastMessageFromSender: boolean
  message: ChatMessage
  size: { width: number; height: number }
}

export function Message({ chatRoomUsers, isLastMessageFromSender, message, size }: MessageProps) {
  const authUser = useAppSelector(selectAuthUser)
  const content = message.content ?? ''
  const isHtml = /^</.test(content.trim())

  return (
    <Box
      sx={{
        display: 'flex',
        alignSelf: message.author === authUser?._id ? 'flex-end' : 'flex-start',
        flexDirection: message.author === authUser?._id ? 'row-reverse' : 'row',
        gap: 1.5,
        mb: 2,
        mx: 1,
      }}
    >
      <AccountCircleIcon
        sx={{
          fontSize: 32,
          color: 'text.secondary',
          alignSelf: 'flex-start',
        }}
      />
      <Box
        sx={{
          maxWidth: '75%',
          minWidth: '120px',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Paper
          sx={{
            p: 1.5,
            bgcolor: message.author === authUser?._id ? 'rgba(220, 248, 198, 1)' : '#ffffff',
            borderRadius: message.author === authUser?._id ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            wordBreak: 'break-word',
            lineHeight: 1.4,
            '& p': { margin: 0, padding: 0 },
            img: {
              maxWidth: size.width * 0.7,
              maxHeight: size.height * 0.7,
              objectFit: 'fill',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography
              variant="caption"
              component="div"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              {new Date(message.date || Date.now())
                .toLocaleTimeString(`${i18n.locale}-${i18n.locale.toUpperCase()}`, {
                  weekday: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })
                .replace(':', 'h')}
            </Typography>
            {message.author !== authUser?._id && <MessageStatusIcon message={message} />}
          </Box>
          <Box className="ql-editor">{isHtml ? <div dangerouslySetInnerHTML={{ __html: content }} /> : <ReactMarkdown>{content}</ReactMarkdown>}</Box>
        </Paper>

        {message.author === authUser?._id && isLastMessageFromSender && message.readBy && <MessageReaders message={message} chatRoomUsers={chatRoomUsers} />}

        {message.author !== authUser?._id && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.7rem',
              mt: 0.5,
            }}
          >
            {getUserName(message.author, chatRoomUsers)}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
